import { useState } from "react"
import * as DropdownMenu from "@radix-ui/react-dropdown-menu"
import { saveAs } from "file-saver"
import { Icon } from "@/components/Icon"
import { useToast } from "@/hooks/use-toast"

/**
 * A single Share control for the order draft. Replaces the standalone DOCX
 * export with a menu: download as Word, download as PDF, or (later) share a
 * link with another officer inside the system.
 *
 * PDF uses the browser's print-to-PDF — a print stylesheet (`body.printing-oio`
 * in na-shell.css) isolates the rendered order document, so the output keeps the
 * exact fonts/formatting (incl. Devanagari) as selectable text without pulling
 * in a heavyweight client-side PDF library.
 */
export function ShareMenu({
  data,
  fileName,
  heldReason,
}: {
  data: string | undefined
  fileName: string
  /**
   * Why the order cannot be exported YET, in the officer's words — a step he
   * must settle before it is signed (features/oio/lib/next-steps.ts). Undefined
   * = cleared. The order itself is on screen either way.
   */
  heldReason?: string
}) {
  const { toast } = useToast()
  const [busy, setBusy] = useState(false)
  const disabled = !data || busy || Boolean(heldReason)

  const downloadWord = async () => {
    if (!data) return
    try {
      setBusy(true)
      const { createGSTOIODocument } = await import("../lib/oio-formatter")
      const blob = await createGSTOIODocument(data)
      saveAs(blob, `${fileName}.docx`)
      toast({ title: "Download started", description: "Your Word document is ready." })
    } catch (err) {
      toast({
        title: "Download failed",
        description: "Could not generate the Word file. Please try again.",
        variant: "destructive",
      })
      console.error("[ShareMenu] docx export failed:", err)
    } finally {
      setBusy(false)
    }
  }

  const downloadPdf = () => {
    if (!data) return
    const cleanup = () => {
      document.body.classList.remove("printing-oio")
      window.removeEventListener("afterprint", cleanup)
    }
    document.body.classList.add("printing-oio")
    window.addEventListener("afterprint", cleanup)
    // Defer so the print stylesheet applies before the dialog opens.
    setTimeout(() => {
      window.print()
      // Fallback cleanup if afterprint doesn't fire (some browsers).
      setTimeout(cleanup, 800)
    }, 60)
  }

  const shareWithOfficer = () => {
    toast({
      title: "Coming soon",
      description:
        "Sharing inside the system is on the way — you'll be able to send a secure link to another officer.",
    })
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="btn btn--primary btn--sm btn--export"
          disabled={disabled}
          title={heldReason || "Share or download the order"}
        >
          {busy ? (
            <span className="spinner" style={{ borderTopColor: "#fff" }} />
          ) : (
            <Icon name="share" size={15} />
          )}
          Share
          <Icon name="chevdown" size={13} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="menu" align="end" sideOffset={6}>
          <DropdownMenu.Item className="menu__item" onSelect={downloadWord}>
            <Icon name="download" size={15} /> Download as Word
          </DropdownMenu.Item>
          <DropdownMenu.Item className="menu__item" onSelect={downloadPdf}>
            <Icon name="filetype" size={15} /> Download as PDF
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="menu__sep" />
          <DropdownMenu.Item className="menu__item" onSelect={shareWithOfficer}>
            <Icon name="users" size={15} /> Share with an officer
            <span className="menu__soon">Soon</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
